import React, { useState } from 'react';

import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@mui/material';

const ENTRIES = [
  {
    id: 'DASI-2041', status: 'error', supplier: 'Airbus Defence & Space', incoterm: 'FCA Toulouse',
    pages: '34 Pages', lines: '542', assist: '$0.00 (None)',
    valMatch: '✓ Verified', valStatus: '0 Flags', valClass: 'dasi-text-green',
    htsMatch: '⚠️ Variance Detected', htsStatus: '2 Flags', htsClass: 'dasi-text-red',
    totalStr: '540 / 542 Lines Clean', badgeStatus: '2 Exceptions Routed', badgeClass: 'dasi-text-red',
    reason: 'CRITICAL HTS MISMATCH: AI mapped semantic description to Chapter 88 (Civil Aircraft - Duty Free). Broker improperly defaulted to generic Chapter 84/85 motor codes carrying a 5% tariff. 540 lines cleared; 2 routed to manual triage.',
    exceptions: [
      { desc: 'PN-8890A Actuator Assembly', ace: '8501.31.40 (5%)', ai: '8803.30.00 (Free)', flag: 'HTS Classification' },
      { desc: 'PN-4412B Titanium Landing Strut', ace: '7326.90.86 (2.9%)', ai: '8803.20.00 (Free)', flag: 'HTS Classification' },
    ],
    btnLines: 540, btnExceptions: 2,
  },
  {
    id: 'DASI-2042', status: 'warning', supplier: 'Safran Landing Systems', incoterm: 'FOB Paris',
    pages: '12 Pages', lines: '128', assist: '$45,000.00 USD',
    valMatch: '⚠️ Assist Missing', valStatus: '1 Flag', valClass: 'dasi-text-yellow',
    htsMatch: '✓ Verified', htsStatus: '0 Flags', htsClass: 'dasi-text-green',
    totalStr: '128 / 128 Lines Clean (Valuation Hold)', badgeStatus: '1 Exception Routed', badgeClass: 'dasi-text-yellow',
    reason: 'VALUATION HOLD (19 CFR § 152.103): AI extracted $45k engineering assist from invoice footnote. Broker filed 7501 based purely on FOB line items, under-declaring the entry value. Routed to manual triage to add assist value before final clearance.',
    exceptions: [
      { desc: 'Engineering Molds (DASI Supplied)', ace: 'NOT DECLARED', ai: '$45,000.00 Addition', flag: 'Undeclared Assist' },
    ],
    btnLines: 128, btnExceptions: 1,
  },
  {
    id: 'DASI-2043', status: 'clean', supplier: 'Rolls-Royce Aviation', incoterm: 'FCA London',
    pages: '45 Pages', lines: '850', assist: '$0.00 (None)',
    valMatch: '✓ Verified', valStatus: '0 Flags', valClass: 'dasi-text-green',
    htsMatch: '✓ Verified', htsStatus: '0 Flags', htsClass: 'dasi-text-green',
    totalStr: '850 / 850 Lines Clean', badgeStatus: '0 Exceptions', badgeClass: 'dasi-text-green',
    reason: "AUTOMATION COMPLETE: 850-line BOM ingested, normalized, and perfectly reconciled against the broker's 7501 filing. Zero classification, valuation, or PGA variances detected. Entry requires zero manual review.",
    exceptions: [
      { desc: 'N/A - All 850 lines perfectly reconciled.', ace: '-', ai: '-', flag: '-' },
    ],
    btnLines: 850, btnExceptions: 0,
  },
];

const DASICompliance = () => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(1);
  const [dismissedFlags, setDismissedFlags] = useState({});
  const [triageState, setTriageState] = useState({});

  const data = ENTRIES[selectedIndex];

  const handleSelectEntry = (index) => {
    setSelectedIndex(index);
    setActiveTab(1);
    setDismissedFlags({});
    setTriageState({});
  };

  const getCatchHeader = (entry) => {
    if (entry.status === 'error') return { text: `⚠️ Triage Required: Entry #${entry.id}`, cls: 'dasi-text-red' };
    if (entry.status === 'warning') return { text: `⚠️ Valuation Triage: Entry #${entry.id}`, cls: 'dasi-text-yellow' };
    return { text: `✓ Zero Triage Required: Entry #${entry.id}`, cls: 'dasi-text-green' };
  };

  const getCatchCardClass = (status) => {
    if (status === 'error') return 'dasi-highlight-row';
    if (status === 'warning') return 'dasi-warning-row';
    return 'dasi-clean-row';
  };

  const getExceptionFlagClass = (status) => {
    if (status === 'error') return 'dasi-text-red';
    if (status === 'warning') return 'dasi-text-yellow';
    return 'dasi-text-green';
  };

  const handleDismiss = (i) => {
    setDismissedFlags(prev => ({ ...prev, [i]: true }));
  };

  const handleTriage = () => {
    setTriageState({ processing: true });
    setTimeout(() => {
      setTriageState({ done: true });
    }, 800);
  };

  const catchHeader = getCatchHeader(data);

  const triageBtnLabel = triageState.done
    ? '✓ Clean Lines Cleared. Exceptions Routed.'
    : triageState.processing
      ? 'Processing Triage...'
      : data.status === 'clean'
        ? `Auto-Approve All ${data.btnLines} Lines`
        : `Clear Clean Lines (${data.btnLines}) & Route Exceptions (${data.btnExceptions})`;

  const triageBtnClass = triageState.done
    ? 'dasi-primary-btn dasi-btn-green'
    : triageState.processing
      ? 'dasi-primary-btn dasi-btn-dark'
      : 'dasi-primary-btn';

  return (
    <Box className="dasi-wrapper">

      {/* Top Bar */}
      {/* <Box className="dasi-topbar">
        <Typography className="dasi-topbar-title">DASI Compliance Hub</Typography>
        <Box className="dasi-badge dasi-badge-indigo">AEROSPACE TRIAGE - LIVE DATA</Box>
      </Box> */}

      {/* Workspace */}
      <Box className="dasi-workspace">

        {/* Queue Panel */}
        <Box
          className="dasi-queue-panel"
          sx={{ opacity: activeTab === 3 ? 0.3 : 1, pointerEvents: activeTab === 3 ? 'none' : 'auto' }}
        >
          <Box className="dasi-queue-header">
            <Typography className="dasi-queue-header-title">Live Entry Queue</Typography>
            <Typography className="dasi-flagged-count">2 Exceptions</Typography>
          </Box>

          {ENTRIES.map((entry, index) => {
            let dotClass = 'dasi-dot-green';
            if (entry.status === 'error') dotClass = 'dasi-dot-red';
            if (entry.status === 'warning') dotClass = 'dasi-dot-yellow';

            return (
              <Box
                key={entry.id}
                className={`dasi-queue-item${selectedIndex === index ? ' dasi-selected' : ''}`}
                onClick={() => handleSelectEntry(index)}
              >
                <Box className="dasi-q-id">
                  <span className={`dasi-status-dot ${dotClass}`} />
                  {entry.id}
                </Box>
                <Box className="dasi-q-supplier">{entry.supplier} | {entry.lines} Lines</Box>
              </Box>
            );
          })}
        </Box>

        {/* Content Panel */}
        <Box className="dasi-content-panel">

          {/* Tab Controls */}
          <Box className="dasi-demo-controls">
            <button
              className={`dasi-demo-btn${activeTab === 1 ? ' dasi-active-tab' : ''}`}
              onClick={() => setActiveTab(1)}
            >
              1. AI BOM Normalization
            </button>
            <button
              className={`dasi-demo-btn${activeTab === 2 ? ' dasi-active-tab' : ''}`}
              onClick={() => setActiveTab(2)}
            >
              2. Compliance Exception Triage
            </button>
            <button
              className={`dasi-demo-btn dasi-impact-tab${activeTab === 3 ? ' dasi-active-tab' : ''}`}
              onClick={() => setActiveTab(3)}
            >
              3. Automation Impact
            </button>
          </Box>

          {/* Screen 1: AI BOM Normalization */}
          {activeTab === 1 && (
            <Box className="dasi-flex-row">

              {/* Left card: Unstructured Invoice Ingestion */}
              <Box className="dasi-card dasi-w-50">
                <Typography className="dasi-card-title">Unstructured Invoice Ingestion</Typography>
                <Box className="dasi-doc-parse">
                  <span className="dasi-parse-blue">[AI Parse: Supplier Header]</span><br />
                  Manufacturer: {data.supplier}<br />
                  Terms: {data.incoterm}<br />
                  End Use: Civil Aircraft (FAA Cert. Pending)<br /><br />
                  <span className="dasi-parse-blue">[AI Parse: Bill of Materials]</span><br />
                  Total Pages Processed: {data.pages}<br />
                  Total Line Items Extracted: {data.lines}<br /><br />
                  <span className="dasi-parse-yellow">[AI Parse: Footnote Extraction (Assists)]</span><br />
                  <em>"Invoice value excludes DASI-supplied engineering molds valued at {data.assist}."</em>
                  <Box className="dasi-source-docs">
                    <span className="dasi-source-label">Source Documents (Secure S3 Vault)</span><br />
                    <button type="button" className="dasi-doc-link">📄 Commercial Invoice (Airbus_CI_8890A.pdf)</button>
                    <button type="button" className="dasi-doc-link">📄 Packing List (Airbus_PL_Box_1_of_4.pdf)</button>
                    <button type="button" className="dasi-doc-link">📄 Air Waybill (AWB_CDG_to_IAD.pdf)</button>
                    <button type="button" className="dasi-doc-link">📄 Draft Form 7501 (Broker_EDI_Intercept.xml)</button>
                    <button type="button" className="dasi-doc-link dasi-doc-link-green">✈️ FAA Form 8130-3 (Airworthiness_Tag.pdf)</button>
                  </Box>
                </Box>
              </Box>

              {/* Right card: Line-Level 7501 Reconciliation */}
              <Box className="dasi-card dasi-w-50">
                <Typography className="dasi-card-title">Line-Level 7501 Reconciliation</Typography>
                <Table className="dasi-table" size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="dasi-th">Compliance Vector</TableCell>
                      <TableCell className="dasi-th">Status</TableCell>
                      <TableCell className="dasi-th">Exceptions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell className="dasi-td">1. Valuation &amp; Assists</TableCell>
                      <TableCell className="dasi-td">{data.valMatch}</TableCell>
                      <TableCell className={`dasi-td ${data.valClass}`}>{data.valStatus}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="dasi-td">2. HTS Classification (Chap. 88)</TableCell>
                      <TableCell className="dasi-td">{data.htsMatch}</TableCell>
                      <TableCell className={`dasi-td ${data.htsClass}`}>{data.htsStatus}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="dasi-td">3. Country of Origin (BOM Match)</TableCell>
                      <TableCell className="dasi-td">✓ Verified</TableCell>
                      <TableCell className="dasi-td dasi-text-green">0 Flags</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="dasi-td">4. Royalties &amp; Non-Dutiable</TableCell>
                      <TableCell className="dasi-td">✓ Verified</TableCell>
                      <TableCell className="dasi-td dasi-text-green">0 Flags</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="dasi-td">5. PGA Flags (FAA / EPA)</TableCell>
                      <TableCell className="dasi-td">✓ Transmitted</TableCell>
                      <TableCell className="dasi-td dasi-text-green">0 Flags</TableCell>
                    </TableRow>
                    <TableRow className="dasi-highlight-value-row">
                      <TableCell className="dasi-td"><strong>Total Auto-Reconciled</strong></TableCell>
                      <TableCell className="dasi-td"><strong>{data.totalStr}</strong></TableCell>
                      <TableCell className={`dasi-td ${data.badgeClass}`}>{data.badgeStatus}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Box className="dasi-triage-action">
                  <button
                    type="button"
                    className={triageBtnClass}
                    onClick={!triageState.done && !triageState.processing ? handleTriage : undefined}
                    disabled={triageState.done || triageState.processing}
                  >
                    {triageBtnLabel}
                  </button>
                </Box>
              </Box>

            </Box>
          )}

          {/* Screen 2: Compliance Exception Triage */}
          {activeTab === 2 && (
            <Box className={`dasi-card ${getCatchCardClass(data.status)}`}>
              <Typography className={`dasi-catch-header ${catchHeader.cls}`}>
                {catchHeader.text}
              </Typography>
              <Table className="dasi-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className="dasi-th">Part Number / Description</TableCell>
                    <TableCell className="dasi-th">ACE 7501 Declared</TableCell>
                    <TableCell className="dasi-th">AI Semantic Extraction</TableCell>
                    <TableCell className="dasi-th">Exception Flag</TableCell>
                    <TableCell className="dasi-th">Resolution Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.exceptions.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell className="dasi-td"><strong>{item.desc}</strong></TableCell>
                      <TableCell className="dasi-td">{item.ace}</TableCell>
                      <TableCell className="dasi-td">{item.ai}</TableCell>
                      <TableCell className={`dasi-td ${getExceptionFlagClass(data.status)}`}>{item.flag}</TableCell>
                      <TableCell className="dasi-td">
                        {item.flag === '-' ? (
                          <span>No Action Needed</span>
                        ) : dismissedFlags[i] ? (
                          <button type="button" className="dasi-resolve-btn dasi-resolve-learned" disabled>
                            ✓ Learned: Business Logic Override
                          </button>
                        ) : (
                          <button type="button" className="dasi-resolve-btn" onClick={() => handleDismiss(i)}>
                            Dismiss / Log False Positive
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography className="dasi-catch-reason">{data.reason}</Typography>
            </Box>
          )}

          {/* Screen 3: Automation Impact */}
          {activeTab === 3 && (
            <Box>
              <Box className="dasi-metric-grid">
                <Box className="dasi-metric-card dasi-metric-green">
                  <Typography className="dasi-metric-label">INVOICE LINES AUTO-RECONCILED</Typography>
                  <Typography className="dasi-metric-value dasi-text-green">4,250</Typography>
                </Box>
                <Box className="dasi-metric-card dasi-metric-red">
                  <Typography className="dasi-metric-label">EXCEPTIONS ROUTED TO TRIAGE</Typography>
                  <Typography className="dasi-metric-value dasi-text-red">14</Typography>
                </Box>
                <Box className="dasi-metric-card dasi-metric-blue">
                  <Typography className="dasi-metric-label">ESTIMATED HOURS SAVED TODAY</Typography>
                  <Typography className="dasi-metric-value dasi-text-blue">6.5 Hours</Typography>
                </Box>
              </Box>
              <Box className="dasi-export-center">
                <button type="button" className="dasi-primary-btn dasi-btn-dark">
                  ⬇ Export Cleared Entry Batch for CBP
                </button>
                <Typography className="dasi-export-sub">
                  Generates final filing packet of all clean, auto-reconciled lines.
                </Typography>
              </Box>
            </Box>
          )}

        </Box>
      </Box>
    </Box>
  );
};

export default DASICompliance;
